<template>
  <li class="member-indoor-item-level-node">
    <div
      v-if="isTarget"
      class="node-contents"
      v-bind:class="{ selected: isSelected }"
      v-bind:style="{ '--padding-left-add': `${indent * 48}px` }"
      v-on:click="$_onItemClick"
    >
      <!-- ラベル -->
      <div class="item-label">
        <div class="arrow-wrapper">
          <arrow-down
            v-if="indoorPlaceNodes.length"
            class="arrow"
            v-bind:class="{ 'is-open': isChildNodeShown }"
            v-on:click.stop="$_onArrowClick"
          />
        </div>
        <div class="item-name">
          {{ name }}
        </div>
      </div>
      <!-- ユーザー数 -->
      <div class="counts">
        <template v-if="showUsersCount">
          <b-row no-gutters class="users-count">
            <user-icon class="user-icon" />
            <span>
              {{ usersCount }}
              {{ $t('appMemberIndoorItemLevelNode.case') }}
            </span>
          </b-row>
          <!-- タグユーザー -->
          <b-row
            v-if="$store.getters['user/hasTagUser']"
            no-gutters
            class="users-count"
          >
            <tag-user-icon class="user-icon" />
            <span>
              {{ tagUsersCount }}
              {{ $t('appMemberIndoorItemSiteNode.case') }}
            </span>
          </b-row>
        </template>
      </div>
    </div>
    <ul v-if="isTarget && indoorPlaceNodes.length" class="children-list">
      <member-indoor-item-place-node
        v-for="indoorPlaceNode in indoorPlaceNodes"
        v-bind:key="`indoor-place-${indoorPlaceNode.id}`"
        v-show="isChildNodeShown"
        v-bind="indoorPlaceNode"
        v-bind:users="users"
        v-bind:selectedId="selectedId"
        v-bind:indent="indent + 1"
        v-on:indoor-item-selected="$_onChildItemSelected"
      />
    </ul>
  </li>
</template>

<script>
import MemberIndoorItemPlaceNode from '@/components/member/AppMemberIndoorItemPlaceNode.vue'
import ArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import UserIcon from '@/assets/ifield/ic_user.svg?component'
import TagUserIcon from '@/assets/ifield/ic_tag_user.svg?component'
import { TAG_USER_ROLE_ID } from 'ifield-core-components'

export default {
  name: 'AppMemberIndoorItemLevelNode',
  components: {
    'member-indoor-item-place-node': MemberIndoorItemPlaceNode,
    'arrow-down': ArrowDown,
    'user-icon': UserIcon,
    TagUserIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * ID
     */
    id: {
      type: Number,
      default: null,
    },
    /**
     * 名称
     */
    name: {
      type: String,
      default: '',
    },
    /**
     * 屋内プレイスノード
     */
    indoorPlaceNodes: {
      type: Array,
      default: () => [],
    },
    /**
     * 検索ターゲットフラグ
     */
    isTarget: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー情報
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択されているID
     */
    selectedId: {
      type: Object,
      default: () => {},
    },
    /**
     * インデント
     */
    indent: {
      type: Number,
      default: 0,
    },
    /**
     * ユーザー数表示フラグ
     */
    showUsersCount: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    /**
     * アイテム選択イベント
     * @param {Object} data 選択情報({ key: id })
     */
    'indoor-item-selected',
  ],
  data: function () {
    return {
      // 子ノードの表示状態
      isChildNodeShown: false,
    }
  },
  computed: {
    /**
     * ユーザー数
     * @type {Number}
     */
    usersCount: function () {
      return this.users.filter(
        (user) =>
          user.indoor_level_id === this.id && user.role_id !== TAG_USER_ROLE_ID
      ).length
    },
    /**
     * タグユーザー数
     * @type {Number}
     */
    tagUsersCount: function () {
      return this.users.filter(
        (user) =>
          user.indoor_level_id === this.id && user.role_id === TAG_USER_ROLE_ID
      ).length
    },
    /**
     * 選択されている場合はtrue
     * @type {Boolean}
     */
    isSelected: function () {
      return this.selectedId.indoorLevel === this.id
    },
  },
  watch: {
    isTarget: {
      immediate: true,
      handler: function (value) {
        if (value === false) {
          this.isChildNodeShown = false
        } else {
          this.isChildNodeShown = this.isSelected
        }
      },
    },
    isSelected: function (value) {
      if (value) {
        this.isChildNodeShown = true
      }
    },
  },
  methods: {
    /**
     * 矢印クリック時処理
     */
    $_onArrowClick: function () {
      this.isChildNodeShown = !this.isChildNodeShown
    },

    /**
     * アイテムクリック時処理
     */
    $_onItemClick: function () {
      const value = this.isSelected ? null : this.id
      this.$_emitSelectedEvent({ indoorLevel: value })
    },

    /**
     * 子アイテム選択時処理
     * @param {Object} data 選択情報({ key: id })
     */
    $_onChildItemSelected: function (data) {
      let result
      const isChildSelected = data && data.indoorPlace
      if (isChildSelected) {
        // 自身も選択されたことにする
        result = { ...data, indoorLevel: this.id }
      } else if (this.isSelected) {
        // 子の選択解除時も自身が選択状態なら維持する
        result = { ...data, indoorLevel: this.id }
      } else {
        result = { ...data }
      }
      this.$_emitSelectedEvent(result)
    },

    /**
     * 選択イベント発行
     * @param {Object} data 選択情報({ key: id })
     */
    $_emitSelectedEvent: function (data) {
      this.$emit('indoor-item-selected', data)
    },
  },
}
</script>

<style lang="scss" scoped>
$-row-height: 32px;

.member-indoor-item-level-node {
  width: 100%;
}

.node-contents {
  --padding-left-add: 0;
  width: 100%;
  min-height: $-row-height;
  padding: 0 8px 0 calc(8px + var(--padding-left-add));
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eaecef;
  user-select: none;
  cursor: pointer;

  &.selected {
    background-color: #f7f7f7;
  }
}

.item-label {
  padding-left: 8px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 4px;
}

.arrow-wrapper {
  width: $-row-height;
  height: $-row-height;

  .arrow {
    width: $-row-height;
    height: $-row-height;
    fill: none;
    stroke: rgba(0, 0, 0, 0.5);
    transform: rotate(-90deg);
    @include mixin-animation(0.1s);

    &:hover {
      stroke: $text-color;
      stroke-width: 1.5;
    }

    &.is-open {
      transform: rotate(0deg);
    }
  }
}

.item-name {
  color: $text-color;
  font-size: $font-size-middle;
  font-weight: $font-weight-bold;
  text-align: left;
  margin: 4px 0;
}

.counts {
  color: $text-color-weak;
  height: 100%;
  font-size: $font-size-small;
  text-align: right;
  margin: 4px 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  row-gap: 4px;

  .row {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .user-icon {
    width: 12px;
    height: 12px;
    fill: #555555;
  }
}

.children-list {
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
